'use client';

import { QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { formatTotemPrice } from '@/features/totems/domain/service/totem-price';
import type { CheckoutQrMode } from '@/features/totems/presentation/components/web-totem/checkout/checkout-flow-reducer';
import { useStaticQr } from '@/features/totems/presentation/hooks/use-static-qr';

type Props = {
  qrMode: CheckoutQrMode;
  qrData: string | null;
  /** Tótem del punto de venta: de ahí sale el QR estático del local. */
  totemId: string | null;
  total: number;
};

/** Espera del pago con QR: estático del local (imagen oficial) o dinámico generado por la terminal. */
export function CheckoutQrPaymentStep({ qrMode, qrData, totemId, total }: Props) {
  const isStatic = qrMode === 'static';
  const { data: staticQr, isLoading: isLoadingStatic } = useStaticQr(totemId ?? '', {
    enabled: isStatic && Boolean(totemId),
  });

  const hasStaticQr = isStatic && staticQr?.ok === true;
  const staticQrFailed = isStatic && !isLoadingStatic && !hasStaticQr;

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-10 text-center">
      <div>
        <h3 className="mb-2 text-2xl font-bold">
          {isStatic ? 'Escaneá el QR del local para pagar' : 'Escaneá el código QR'}
        </h3>
        <p className="text-sm text-muted-foreground">
          {isStatic
            ? 'El cliente escanea el código de Mercado Pago del punto de venta con su app.'
            : 'El cliente escanea el código con su app de Mercado Pago.'}
        </p>
      </div>

      <div className="flex items-center gap-2 rounded-full border bg-muted/30 px-5 py-2">
        <span className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Total a pagar</span>
        <span className="text-xl font-black text-orange-500">{formatTotemPrice(total)}</span>
      </div>

      {isStatic ? (
        isLoadingStatic ? (
          <Skeleton className="size-64 rounded-2xl" />
        ) : hasStaticQr ? (
          <div className="rounded-2xl border-4 border-muted bg-white p-4">
            {staticQr.kind === 'image_url' ? (
              // La imagen oficial del POS llega como data URL/base64: `next/image` no aporta acá.
              <img
                src={staticQr.value}
                alt="QR de Mercado Pago"
                width={256}
                height={256}
                className="size-64 object-contain"
              />
            ) : (
              <QRCodeSVG value={staticQr.value} size={256} />
            )}
          </div>
        ) : (
          <div className="flex size-64 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed bg-muted/20 p-6">
            <QrCode className="size-10 text-muted-foreground" />
            <p className="text-sm font-medium text-muted-foreground">
              {staticQrFailed && staticQr && !staticQr.ok
                ? staticQr.message
                : 'Mostrá el QR impreso de este punto de venta para completar el pago.'}
            </p>
          </div>
        )
      ) : qrData ? (
        <div className="rounded-2xl border-4 border-muted bg-white p-6">
          <QRCodeSVG value={qrData} size={256} />
        </div>
      ) : (
        <div className="flex size-64 animate-pulse items-center justify-center rounded-2xl border bg-muted/20">
          <span className="text-muted-foreground">Generando código...</span>
        </div>
      )}

      <div className="mx-auto flex w-full max-w-xs items-center justify-between rounded-full border border-orange-200 bg-orange-50 px-6 py-3 text-orange-600 shadow-sm dark:border-orange-800/50 dark:bg-orange-900/30 dark:text-orange-400">
        <Spinner className="size-5 shrink-0 bg-orange-600 dark:bg-orange-400" />
        <span className="font-medium">Esperando pago...</span>
        <div className="size-5 shrink-0" />
      </div>
    </div>
  );
}
